import { useCallback, useMemo, useState } from "react";

interface ChecklistItem {
  id: string;
  checked: boolean;
}

type UseCheckListOptions = {
  initialItems?: ChecklistItem[];
};

interface CheckListControls {
  isAllChecked: boolean;
  items: ChecklistItem[];
  check(id: string): boolean;
  uncheck(id: string): boolean;
  toggle(id: string): boolean;
  allCheck(): void;
  allUncheck(): void;
  addItem(item: Omit<ChecklistItem, "checked">): void;
  removeItem(id: string): void;
  getCheckedItems(): ChecklistItem[];
  isChecked(id: string): boolean;
}

export function useChecklist({ initialItems = [] }: UseCheckListOptions = {}): CheckListControls {
  const [items, setItems] = useState<ChecklistItem[]>(initialItems);

  const isAllChecked = useMemo(() => {
    return items.length > 0 && items.every((item) => item.checked);
  }, [items]);

  const toggle = useCallback((id: string): boolean => {
    let found = false;

    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === id) {
          found = true;
          return { ...item, checked: !item.checked };
        }
        return item;
      }),
    );

    return found;
  }, []);

  // Check an item
  const check = useCallback((id: string): boolean => {
    let found = false;

    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === id) {
          found = true;
          return { ...item, checked: true };
        }
        return item;
      }),
    );

    return found;
  }, []);

  // Uncheck an item
  const uncheck = useCallback((id: string): boolean => {
    let found = false;

    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === id) {
          found = true;
          return { ...item, checked: false };
        }
        return item;
      }),
    );

    return found;
  }, []);

  // Check all items
  const allCheck = useCallback(() => {
    setItems((prevItems) => prevItems.map((item) => ({ ...item, checked: true })));
  }, []);

  // Uncheck all items
  const allUncheck = useCallback(() => {
    setItems((prevItems) => prevItems.map((item) => ({ ...item, checked: false })));
  }, []);

  // Add a new item
  const addItem = useCallback((item: Omit<ChecklistItem, "checked">) => {
    setItems((prevItems) => [...prevItems, { ...item, checked: false }]);
  }, []);

  // Remove an item
  const removeItem = useCallback((id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
  }, []);

  // Get all checked items
  const getCheckedItems = useCallback(() => {
    return items.filter((item) => item.checked);
  }, [items]);

  // Check if an item is checked
  const isChecked = useCallback(
    (id: string) => {
      return items.some((item) => item.id === id && item.checked);
    },
    [items],
  );

  return {
    isAllChecked,
    items,
    check,
    uncheck,
    toggle,
    allCheck,
    allUncheck,
    addItem,
    removeItem,
    getCheckedItems,
    isChecked,
  };
}
